import { c as _c } from "react/compiler-runtime";
import { LayoutAuth } from "@/components/layout-auth";
import { CompanyInfo } from "@/components/auth-flow/signup/company-info";
import { PricingTier } from "@/components/auth-flow/signup/constants";
import { Pricing } from "@/components/pricing/pricing";
import { Purchase } from "@/components/auth-flow/signup/purchase";
import { AsidePanel } from "@/components/aside/aside-panel";
import { Signup } from "@/components/auth-flow/signup/signup";
import { BillingInterval, SubscriptionTier } from "@/components/auth-flow/signup/types";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import Script from "next/script";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { CaptchaProvider } from "@/components/auth-flow/captcha-provider";
import { track } from "@/lib/mixpanel-client";
import { ArrowLeft } from "lucide-react";
import { LaunchDarklyProvider } from "@/contexts/launchdarkly-context";
import { MotionConfig } from "motion/react";
import { pushToDataLayer } from "@/components/auth-flow/signup/push-to-data-layer";
import { getChargedBillingInterval } from "@/components/auth-flow/signup/get-charged-billing-interval";
const GTM_ID = "GTM-NFF9LV6V";
export { getSignupServerSideProps as getServerSideProps } from "@/components/auth-flow/signup/get-signup-props";
export default function SignupPage() {
  const $ = _c(58);
  const [step, setStep] = useState("signup");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingInterval, setSelectedBillingInterval] = useState(null);
  const [isAdjustPlanModalOpen, setIsAdjustPlanModalOpen] = useState(false);
  const router = useRouter();
  let t0;
  let t1;
  if ($[0] !== router.query) {
    t0 = () => {
      const urlParams = new URLSearchParams(router.query as Record<string, string>);
      const paramStep = urlParams.get("step");
      const paramPlan = urlParams.get("plan");
      const paramBillingInterval = urlParams.get("billing") as BillingInterval;
      const steps = ["signup", "pricing", "purchase", "company-info", "account-type-selection"] as const;
      if (paramStep && steps.includes(paramStep as (typeof steps)[number])) {
        setStep(paramStep as (typeof steps)[number]);
      }
      if (paramPlan) {
        setSelectedPlan(paramPlan.toUpperCase() as SubscriptionTier);
      }
      if (paramBillingInterval) {
        setSelectedBillingInterval(paramBillingInterval.toUpperCase() as BillingInterval);
      }
      track("Signup / Landing / Page View", {
        plan: paramPlan,
        billingInterval: paramBillingInterval
      });
    };
    t1 = [router.query];
    $[0] = router.query;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== step) {
    t2 = () => {
      if (step === "company-info") {
        window.scrollTo(0, 0);
      }
    };
    t3 = [step];
    $[3] = step;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (tierId, billingInterval) => {
      setSelectedPlan(tierId);
      setSelectedBillingInterval(billingInterval as BillingInterval);
      setStep(_temp);
      track("Signup / Pricing / Plan Selected", {
        plan: tierId,
        billingInterval
      });
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleSelectPlan = t4;
  let t5;
  if ($[7] !== selectedBillingInterval || $[8] !== selectedPlan) {
    t5 = () => {
      if (selectedPlan && selectedBillingInterval) {
        setStep("purchase");
      } else {
        setStep("pricing");
      }
      track("Signup / Register / Success", {});
      pushToDataLayer({
        event: "signup_account_created"
      });
    };
    $[7] = selectedBillingInterval;
    $[8] = selectedPlan;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSignupSuccess = t5;
  let t6;
  if ($[10] !== selectedBillingInterval || $[11] !== selectedPlan) {
    t6 = () => {
      setStep("company-info");
      const billingInterval_0 = selectedPlan && selectedBillingInterval ? getChargedBillingInterval(selectedPlan, selectedBillingInterval) : selectedBillingInterval;
      track("Signup / Purchase / Success", {
        plan: selectedPlan,
        billingInterval: billingInterval_0
      });
      pushToDataLayer({
        event: "signup_purchase_success",
        plan: selectedPlan,
        billingInterval: billingInterval_0
      });
    };
    $[10] = selectedBillingInterval;
    $[11] = selectedPlan;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handlePaymentSuccess = t6;
  const t7 = selectedPlan as PricingTier["id"];
  const t8 = selectedBillingInterval as BillingInterval;
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = {
      tierId: t7,
      billingInterval: t8
    };
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const currentPlan = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Script id="gtm" strategy="afterInteractive" dangerouslySetInnerHTML={{
      __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${GTM_ID}');
            `
    }} />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <noscript><iframe src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`} height="0" width="0" style={{
        display: "none",
        visibility: "hidden"
      }} title="Google Tag Manager" /></noscript>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== handleSignupSuccess || $[19] !== step) {
    t12 = step === "signup" ? <Signup onSignupSuccess={handleSignupSuccess} /> : null;
    $[18] = handleSignupSuccess;
    $[19] = step;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== handlePaymentSuccess || $[22] !== selectedBillingInterval || $[23] !== selectedPlan || $[24] !== step) {
    t13 = step === "purchase" && selectedPlan && selectedBillingInterval ? <Purchase selectedPlan={selectedPlan} billingInterval={selectedBillingInterval} onChangePlan={() => {
      setIsAdjustPlanModalOpen(true);
      track("Signup / Purchase / Modal Opened", {
        plan: selectedPlan,
        billingInterval: selectedBillingInterval
      });
    }} onPaymentSuccess={handlePaymentSuccess} /> : null;
    $[21] = handlePaymentSuccess;
    $[22] = selectedBillingInterval;
    $[23] = selectedPlan;
    $[24] = step;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== step) {
    t14 = step === "company-info" ? <CompanyInfo /> : null;
    $[26] = step;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = step === "pricing" && !selectedPlan && !selectedBillingInterval;
  let t16;
  if ($[28] !== selectedBillingInterval || $[29] !== selectedPlan) {
    t16 = () => {
      setStep("purchase");
      track("Signup / Pricing / Modal Closed", {
        plan: selectedPlan,
        billingInterval: selectedBillingInterval
      });
    };
    $[28] = selectedBillingInterval;
    $[29] = selectedPlan;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DialogTitle className="sr-only">Change Plan</DialogTitle>;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      background: "linear-gradient(184deg, #FFF 3.77%, #FFFDFB 12.77%, #FCF9F5 26.06%, #F9F4EF 48.35%, #F4EBE4 92.93%)"
    };
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== currentPlan) {
    t19 = <DialogContent CloseButton={null} className="w-full h-full max-w-screen transition-none duration-0 overflow-y-auto bg-background-muted" style={t18}><Pricing title="No contracts, no gotchas, grow 10% in your first year or your money back" onSelectPlan={handleSelectPlan} currentPlan={currentPlan} /></DialogContent>;
    $[33] = currentPlan;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== t19) {
    t20 = <Dialog open={t15} onOpenChange={t16}>{t17}{t19}</Dialog>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <DialogTitle className="sr-only">Change Plan</DialogTitle>;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      background: "linear-gradient(184deg, #FFF 3.77%, #FFFDFB 12.77%, #FCF9F5 26.06%, #F9F4EF 48.35%, #F4EBE4 92.93%)"
    };
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="absolute top-4 left-4 md:left-20 text-brand-primary-950 text-sm flex items-center gap-2 cursor-pointer" onClick={() => setIsAdjustPlanModalOpen(false)}><ArrowLeft className="w-4 h-4" />Back</div>;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = (tierId_0, billingInterval_1) => {
      handleSelectPlan(tierId_0, billingInterval_1);
      setIsAdjustPlanModalOpen(false);
    };
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsAdjustPlanModalOpen(false);
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== currentPlan) {
    t26 = <DialogContent CloseButton={null} className="w-full h-full max-w-screen transition-none duration-0 overflow-y-auto bg-background-muted" style={t22}>{t23}<Pricing title="Adjust your plan" onSelectPlan={t24} currentPlan={currentPlan} onClose={t25} /></DialogContent>;
    $[44] = currentPlan;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== isAdjustPlanModalOpen || $[47] !== t26) {
    t27 = <Dialog open={isAdjustPlanModalOpen} onOpenChange={setIsAdjustPlanModalOpen}>{t21}{t26}</Dialog>;
    $[46] = isAdjustPlanModalOpen;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== t12 || $[50] !== t13 || $[51] !== t14 || $[52] !== t20 || $[53] !== t27) {
    t28 = <>{t12}{t13}{t14}{t20}{t27}</>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t20;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <AsidePanel />;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t28) {
    t30 = <MotionConfig reducedMotion="user"><CaptchaProvider><LaunchDarklyProvider>{t10}{t11}<LayoutAuth primary={t28} secondary={t29} /></LaunchDarklyProvider></CaptchaProvider></MotionConfig>;
    $[56] = t28;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  return t30;
}
function _temp(prev) {
  return prev === "pricing" ? "purchase" : prev;
}